/**
 * Provider List
 * Displays list of configured authentication providers
 */

import { Add as AddIcon, Cloud as CloudIcon, Settings as SettingsIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  Card,
  List,
  Divider,
  Button,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthProviderConfig, ProviderTemplates } from '../types';
import { ProviderListItem } from './ProviderListItem';

interface ProviderListProps {
  providers: AuthProviderConfig[];
  templates: ProviderTemplates;
  primaryProvider: string;
  onEdit: (provider: AuthProviderConfig) => void;
  onDelete: (provider: AuthProviderConfig) => void;
  onToggle: (providerType: string, enabled: boolean) => void;
  onSetPrimary: (providerType: string) => void;
  onAdd: () => void;
  loading: boolean;
}

/**
 * Renders the list of authentication providers or empty state
 */
export const ProviderList: React.FC<ProviderListProps> = ({
  providers,
  templates,
  primaryProvider,
  onEdit,
  onDelete,
  onToggle,
  onSetPrimary,
  onAdd,
  loading,
}) => {
  const { t } = useTranslation();

  if (providers.length === 0) {
    return (
      <Card>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CloudIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('auth.providers.noProviders')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.providers.noProvidersDescription')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
            disabled={loading}
          >
            {t('auth.providers.addFirstProvider')}
          </Button>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <List>
        {providers.map((provider, index) => {
          const template =
            templates[provider.provider_type] || {
              name: provider.provider_type,
              description: '',
              icon: <SettingsIcon />,
              fields: [],
            };

          return (
            <React.Fragment key={`${provider.provider_type}-${index}`}>
              <ProviderListItem
                provider={provider}
                template={template}
                isPrimary={provider.provider_type === primaryProvider}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggle={onToggle}
                onSetPrimary={onSetPrimary}
                loading={loading}
              />
              {index < providers.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </Card>
  );
};
